import { Box, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { Avatar, ChipV2, CircleCheckbox, InfoIcon, Tooltip } from "components";
import { CloseIcon, MailIcon } from "components/icons";
import { memo, useCallback, useMemo } from "react";
import type { ChatMember } from "shared";

// import { MemberTag } from "./MemberTag";

interface MemberListItemProps {
  member: ChatMember;
  isSelected: boolean;
  onToggle: (userId: string) => void;
  level: "ticket" | "thread";
  /** Show "Limited Access" chip and disable selection */
  isLimitedAccess?: boolean;
  /** Show "Inactive" chip and disable selection */
  isInactive?: boolean;
  /** When provided, MemberTag becomes a dropdown to switch levels */
  onLevelChange?: (userId: string, level: "ticket" | "thread") => void;
  /** When false, hides the checkbox. Defaults to true */
  selectable?: boolean;
  /** When provided, shows a close icon to remove non-core members */
  onRemove?: (userId: string) => void;
  /** When true, hides the email subtitle */
  hideEmail?: boolean;
}

/**
 * MemberListItem - Individual member row in the drawer
 * States:
 * - Selected (Ticket Level): Green background, blue tag, visible checkbox
 * - Selected (Thread Level): Green background, yellow tag, visible checkbox
 * - Unselected: White background, hidden tag/checkbox (shown on hover)
 * - Disabled: Neutral bg, not-allowed cursor, info icon tooltip (core members, limited access, inactive)
 */
export const MemberListItem = memo(
  ({
    member,
    isSelected,
    onToggle,
    isLimitedAccess,
    isInactive,
    level,
    onLevelChange,
    selectable = true,
    onRemove,
    hideEmail,
  }: MemberListItemProps) => {
    const theme = useTheme();

    const isCore = member.member_source === "core";
    const isDisabled = isCore || isLimitedAccess || isInactive;

    const handleClick = useCallback(() => {
      if (!isDisabled) {
        onToggle(member.user_id);
      }
    }, [isDisabled, member.user_id, onToggle]);

    // const handleLevelChange = useCallback(
    //   (newLevel: "ticket" | "thread") => {
    //     onLevelChange?.(member.user_id, newLevel);
    //   },
    //   [member.user_id, onLevelChange],
    // );

    const disabledReason = useMemo(() => {
      if (isCore && member.member_type === "resource")
        return "Assignee cannot be removed";
      if (isCore && member.member_type === "customer")
        return "Ticket user cannot be removed";
      if (isCore) return "Core member cannot be removed";
      if (isLimitedAccess)
        return "This member has limited access to this customer";
      if (isInactive) return "This member is inactive";
      return "";
    }, [isCore, member.member_type, isLimitedAccess, isInactive]);

    const displayName = member.display_name || member.email || "Unknown";

    const rowStyles = useMemo(() => {
      if (!selectable) {
        return {
          bg: theme.palette.neutral.main,
          cursor: "default" as const,
          hover: {
            backgroundColor: theme.palette.neutral["005"],
            "& .member-type-chips": { opacity: 1 },
            "& .member-remove-btn": { opacity: 1 },
          },
        };
      }
      const bg =
        isDisabled && isSelected
          ? theme.palette.neutral["005"]
          : isSelected
            ? theme.palette.primitive.brand.purple[25]
            : theme.palette.neutral.main;
      return {
        bg,
        cursor: (isDisabled ? "not-allowed" : "pointer") as
          | "not-allowed"
          | "pointer",
        hover: isDisabled
          ? {
              backgroundColor: theme.palette.neutral["005"],
              "& .member-type-chips": { opacity: 1 },
            }
          : {
              backgroundColor: theme.palette.neutral["005"],
              "& .member-tag": { opacity: 1 },
              "& .member-type-chips": { opacity: 1 },
            },
      };
    }, [selectable, isDisabled, isSelected, theme]);

    const row = (
      <Box
        onClick={selectable ? handleClick : undefined}
        sx={{
          height: "48px",
          padding: "12px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: rowStyles.cursor,
          backgroundColor: rowStyles.bg,
          gap: "8px",
          "&:hover": rowStyles.hover,
        }}
      >
        {/* Left section: Avatar + Name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flex: 1,
            minWidth: 0,
          }}
        >
          {member.member_type === "external" ? (
            <Stack
              sx={{
                width: "24px",
                height: "24px",
                borderRadius: "100%",
                bgcolor: theme.palette.neutral["010"],
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <MailIcon sx={{ fontSize: "12px" }} />
            </Stack>
          ) : (
            <Avatar
              src={member.profile_pic || ""}
              fullname={displayName}
              variants="sm"
            />
          )}

          <Stack spacing="4px" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography
                variant="body-small"
                sx={{
                  color: theme.palette.lightBg.medium,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </Typography>
            </Box>
            {!hideEmail && member.email && member.display_name && (
              <Typography
                variant="body-xsmall"
                sx={{
                  color: theme.palette.lightBg.low,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {member.email}
              </Typography>
            )}
          </Stack>
          <Box
            className="member-type-chips"
            sx={{
              display: "flex",
              gap: "4px",
              flexShrink: 0,
              opacity: 0,
              transition: "opacity 0.2s",
            }}
          >
            {member.member_type === "external" && (
              <ChipV2
                containerSx={{
                  backgroundColor: theme.palette.primitive.system.orange[50],
                }}
                variant="neutral"
              >
                External
              </ChipV2>
            )}
            {member.member_type === "resource" && (
              <ChipV2
                containerSx={{
                  backgroundColor: theme.palette.primitive.system.violet[100],
                }}
                variant="neutral"
              >
                Team member
              </ChipV2>
            )}
            {member.member_type === "customer" && (
              <ChipV2
                containerSx={{
                  backgroundColor: theme.palette.primitive.system.blue[200],
                }}
                variant="neutral"
              >
                User
              </ChipV2>
            )}
          </Box>
        </Box>
        {isCore && (
          <Tooltip title={disabledReason} placement="top">
            <Box>
              <InfoIcon sx={{ fontSize: "16px" }} />
            </Box>
          </Tooltip>
        )}
        {/* Right section: Chips + Tag + Checkbox */}

        {selectable && (
          <Box
            className="member-type-chips"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              opacity: 0,
              transition: "opacity 0.2s",
            }}
          >
            {isInactive && <ChipV2 variant="info">Inactive user</ChipV2>}
            {isLimitedAccess && !isInactive && (
              <ChipV2 variant="warning">Access restricted</ChipV2>
            )}

            {/* {!isLimitedAccess && !isInactive && (
            <Box
              className="member-tag"
              sx={{
                opacity: !selectable || isSelected ? 1 : 0,
                transition: "opacity 0.2s",
              }}
            >
              <MemberTag
                level={level}
                onLevelChange={
                  selectable && onLevelChange ? handleLevelChange : undefined
                }
              />
            </Box>
          )} */}
          </Box>
        )}

        {selectable && (
          <Box
            className="member-checkbox"
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <CircleCheckbox
              checked={isSelected}
              disabled={isDisabled}
              aria-label={`Select ${displayName}`}
              onChange={handleClick}
              size="sm"
            />
          </Box>
        )}

        {!selectable && onRemove && !isCore && (
          <IconButton
            className="member-remove-btn"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(member.user_id);
            }}
            aria-label={`Remove ${displayName}`}
            sx={{
              opacity: 0,
              transition: "opacity 0.2s",
              padding: "0px",
              color: theme.palette.lightBg.low,
              "&:hover": {
                color: theme.palette.lightBg.medium,
                backgroundColor: "transparent",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        )}
      </Box>
    );

    return row;
  },
);

MemberListItem.displayName = "MemberListItem";
